import React, { PropsWithChildren } from 'react';
import { gql } from '@common/hooks';
import { Typography, Col, Row } from 'antd';
import { AntBreakpoints } from 'works/legacy/lib/style-helper';
import { Colors, FontSize } from '@common/tokens';
import {
  TimeWithTimeZone,
  WorksAvatar,
  Icon,
  TextOverflow,
} from '@common/components';
import { DateOfBirthLink, SsnLink } from '@clinician-profile/components';
import EditEmployeeId from 'works/legacy/components/edit-organization-profile/edit-employee-id';
import { ProfileStatusSetupLink } from '@working-experience/components';
import Auth from 'works/legacy/lib/auth';

const { Title } = Typography;

type Props = {
  worksProfile: {
    id: string;
    phone: string;
    email: string;
    profilePic: string;
    firstName: string;
    lastName: string;
    fullName: string;
    canShowPhi: {
      value: boolean;
    };
    role: {
      id: string;
      name: string;
    };
    primarySpecialty: {
      id: string;
      displayName: string;
    };
    secondarySpecialties: {
      id: string;
      displayName: string;
    }[];
    identificationCode: string;
  };
  submissionTime: string;
  supplier: {
    name: string;
  };
  assignment: {
    id: string;
    organizationProfile: {
      id: string;
      employeeId: string;
      canUpdate: {
        value: boolean;
      };
    };
  };
  application: {
    id: string;
    doNotRehire: boolean;
  };
};

export function ApplicationNurseProfileHeader({
  worksProfile,
  children,
  supplier,
  submissionTime,
  assignment,
  application,
}: PropsWithChildren<Props>) {
  const secondarySpecialties = worksProfile.secondarySpecialties
    .map((specialty) => specialty.displayName)
    .join(', ');

  return (
    <section className="nurse-profile-header">
      <Col span={14} className="nurse-profile-content">
        <Row gutter={[24, 0]} align="middle">
          <Col className="profile-avatar-area">
            <WorksAvatar
              avatarImageSrc={worksProfile.profilePic}
              firstName={worksProfile.firstName}
              lastName={worksProfile.lastName}
              size={112}
            />
          </Col>
          <Col className="profile-information">
            <Title level={3} className="profile-title">
              {worksProfile.fullName}
            </Title>

            {submissionTime && (
              <div className="submission-time">
                Submitted{' '}
                <TimeWithTimeZone date={submissionTime} variant="long_format" />
              </div>
            )}
            {Auth.organizationMember() && (
              <ProfileStatusSetupLink
                applicationId={application.id}
                doNotRehire={application.doNotRehire}
                profileIdentificationCode={worksProfile.identificationCode}
              />
            )}
            <Row gutter={[32, 0]}>
              <Col>
                <ul className="profile-contact fa-ul">
                  {supplier && (
                    <li>
                      <span className="fa-li profile-contact-header">
                        <Icon
                          icon="building"
                          kind="light"
                          size="sm"
                          tooltipText="Supplier"
                        />
                      </span>
                      From {supplier.name}
                    </li>
                  )}
                  <li className="sentry-mask">
                    <span className="fa-li profile-contact-header">
                      <Icon
                        icon="mobile"
                        kind="light"
                        size="sm"
                        tooltipText="Mobile Number"
                      />{' '}
                    </span>
                    {worksProfile.phone}
                  </li>
                  <li className="sentry-mask">
                    <span className="fa-li profile-contact-header">
                      <Icon
                        icon="envelope"
                        kind="light"
                        size="sm"
                        tooltipText="Email"
                      />{' '}
                    </span>
                    {worksProfile.email}
                  </li>

                  {worksProfile && worksProfile.canShowPhi.value && (
                    <>
                      <li>
                        <span className="fa-li profile-contact-header">
                          <Icon
                            icon="birthdayCake"
                            kind="light"
                            size="sm"
                            tooltipText="Birthdate"
                          />{' '}
                        </span>
                        <DateOfBirthLink worksProfileId={worksProfile.id} />
                      </li>
                      <li className="sentry-mask">
                        <span className="fa-li profile-contact-header">
                          <Icon
                            icon="idCard"
                            kind="light"
                            size="sm"
                            tooltipText="Social Security Number"
                          />{' '}
                        </span>
                        <SsnLink
                          worksProfileId={worksProfile.id}
                          canShowPhi={worksProfile.canShowPhi.value}
                        />
                      </li>
                    </>
                  )}

                  {assignment?.organizationProfile && (
                    <li>
                      <EditEmployeeId
                        employeeId={assignment.organizationProfile.employeeId}
                        organizationProfileId={
                          assignment.organizationProfile.id
                        }
                        canUpdate={
                          assignment.organizationProfile.canUpdate.value
                        }
                      />
                    </li>
                  )}
                </ul>
              </Col>
              <Col>
                <ul className="profile-contact fa-ul">
                  {worksProfile.role?.name && (
                    <li>
                      <span className="fa-li profile-contact-header">
                        <Icon
                          icon="userNurse"
                          kind="light"
                          size="sm"
                          tooltipText="Role"
                        />
                      </span>
                      <span>{worksProfile.role.name}</span>
                    </li>
                  )}
                  {worksProfile.primarySpecialty?.displayName && (
                    <li>
                      <span className="fa-li">
                        <Icon
                          icon="bedPulse"
                          kind="light"
                          size="sm"
                          tooltipText="Specialty"
                        />
                        {'\u00A0'}
                      </span>
                      <span>{worksProfile.primarySpecialty.displayName}</span>
                    </li>
                  )}
                  <li>
                    {secondarySpecialties.length < 70 ? (
                      <span>{secondarySpecialties}</span>
                    ) : (
                      <TextOverflow tooltip={secondarySpecialties}>
                        {`${secondarySpecialties.slice(0, 70)}...`}
                      </TextOverflow>
                    )}
                  </li>
                </ul>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
      <Col span={10} className="nurse-profile-children">
        {children}
      </Col>
      <style jsx>{`
        .profile-contact.fa-ul > li {
          margin-top: 2px;
        }
        .submission-time {
          font-size: ${FontSize.xs};
          color: ${Colors.midnightLighter};
        }
        .nurse-profile-header {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          margin-bottom: 2rem;
        }
        .nurse-profile-content {
          flex-grow: 1;
          display: flex;
          align-items: center;
        }
        .profile-contact {
          margin-left: 20px;
          line-height: 1.5em;
        }
        .profile-avatar-area {
          position: relative;
          text-align: center;
          margin-right: 24px;
          min-width: 112px;
          max-width: 112px;
        }
        .profile-avatar-area :global(.ant-tag) {
          position: absolute;
          bottom: -12px;
          left: 50%;
          transform: translateX(-50%);
        }
        .ant-row.profile-subrow {
          margin-bottom: 15px;
        }
        .profile-proftitles {
          font-size: ${FontSize.lg};
          margin-left: 0.25em;
        }
        .nurse-profile-children {
          flex-shrink: 1;
          flex-basis: 25%;
        }

        @media (min-width: ${AntBreakpoints.lgPx}) {
          .nurse-profile-header {
            flex-wrap: nowrap;
          }
          .nurse-profile-children {
            flex-grow: 0;
          }
        }
      `}</style>
      <style jsx>{`
        :global(h3.ant-typography.profile-title) {
          display: inline-block;
          margin-bottom: 0;
          margin-right: 0.25em;
        }
      `}</style>
    </section>
  );
}

ApplicationNurseProfileHeader.fragments = {
  worksProfile: gql`
    fragment NurseProfileHeaderWorksProfileFragment on WorksProfile {
      id
      phone
      email
      profilePic
      firstName
      lastName
      fullName
      canShowPhi {
        value
      }
      role {
        id
        name
      }
      primarySpecialty {
        id
        displayName
      }
      secondarySpecialties {
        id
        displayName
      }
    }
  `,
  staffingRequestAssignment: gql`
    fragment NurseProfileHeaderStaffingRequestAssigmentFragment on StaffingRequestAssignment {
      id
      organizationProfile {
        id
        canUpdate {
          value
        }
      }
    }
  `,
};
